import { clubPath, clubUrl } from '../clubhouse';
import React, { useState, useEffect } from 'react';
import { useNavigate, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  Flag, ChevronLeft, Calendar, BookOpen, Users, Euro, BarChart3,
  Building2, ChevronDown, Check, Globe, LogOut, Menu, X, Newspaper, Home, MessageSquare, Settings, PiggyBank, Trophy, Brain, ShoppingBag, Target, Mail, GraduationCap, LineChart, Send, Bell, UtensilsCrossed, Megaphone, FileText, ExternalLink, Receipt, Search, History, ClipboardList, ShoppingCart, Gift, Package, Shield
} from 'lucide-react';
import { orgs, auth } from '../services/api';
import { fetchSidebarCounts, subscribe as subscribeSidebarCounts } from '../services/sidebarCountsService';
import { toast } from 'sonner';
import NotificationInbox from '../components/NotificationInbox';
import AdminControlCenterBell from '../components/AdminControlCenterBell';
import TaskReminderModal from '../components/TaskReminderModal';

// Sous-pages ouvertes depuis une page du menu : autorisées avec leur page parente (droits « par page »)
const GOLF_SUBPAGE_PARENT = {
  encaissement: 'caisse', 'comptes-clients': 'caisse', 'bons-cadeaux': 'caisse', tarifs: 'caisse', 'prelevements-sepa': 'caisse',
  'types-clients': 'clients', 'appels-cotisation': 'clients',
  cours: 'planning', 'course-config': 'planning',
  'green-fees': 'portal-settings', mews: 'portal-settings', seo: 'site-vitrine',
  news: 'actualites', mailing: 'email-management',
  rapports: 'paiements', 'pack-anomalies': 'control-center',
};


const GolfModule = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, refreshUser } = useAuth();
  const [organizations, setOrganizations] = useState([]);
  const [showOrgDropdown, setShowOrgDropdown] = useState(false);
  const [switchingOrg, setSwitchingOrg] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false); // Menu hamburger mobile
  const [desktopCollapsed, setDesktopCollapsed] = useState(false); // Sidebar réduite desktop
  const [unreadRemarksCount, setUnreadRemarksCount] = useState(0);
  const [newFittingOrdersCount, setNewFittingOrdersCount] = useState(0);
  const [newCompRegistrations, setNewCompRegistrations] = useState(0);
  const [newStageRegistrations, setNewStageRegistrations] = useState(0);
  const [newPaymentsCount, setNewPaymentsCount] = useState(0);
  const [newRestaurantCount, setNewRestaurantCount] = useState(0);
  const [proTasksPendingCount, setProTasksPendingCount] = useState(0);
  // Notifications admin (signatures contrats/mandats SEPA)
  const [clientNotifsTotal, setClientNotifsTotal] = useState(0);
  // Centre de contrôle : total anomalies (critical + warning)
  const [controlCenterCount, setControlCenterCount] = useState(0);
  const [controlCenterCritical, setControlCenterCritical] = useState(0);

  // Mettre à jour le manifest PWA pour l'espace Pro
  useEffect(() => {
    // Changer le manifest pour l'espace Pro
    const manifestLink = document.querySelector('link[rel="manifest"]');
    if (manifestLink) {
      manifestLink.href = '/api/golf/manifest.json';
    }
    
    // Changer le titre
    document.title = 'Golf Pro - Kempferhof';
    
    // Changer l'apple-touch-icon
    const appleIcon = document.querySelector('link[rel="apple-touch-icon"]');
    if (appleIcon) {
      appleIcon.href = '/api/golf/pro-logo.png';
    }
    
    // Changer le meta apple-mobile-web-app-title
    const appleTitle = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    if (appleTitle) {
      appleTitle.content = 'Golf Pro';
    }
    
    // Cleanup: remettre le manifest client quand on quitte
    return () => {
      if (manifestLink) {
        manifestLink.href = '/api/client-portal/manifest.json';
      }
      const appleIconClean = document.querySelector('link[rel="apple-touch-icon"]');
      if (appleIconClean) {
        appleIconClean.href = '/api/client-portal/logo.png';
      }
    };
  }, []);

  // Charger les compteurs au montage et quand user change
  // iter319 — Remplace les 8 fetches individuels par 1 seul GET /api/admin/sidebar-counts
  useEffect(() => {
    if (!user) return;

    loadOrganizations();

    // Appliquer les data à nos states
    const applyCounts = (data) => {
      if (!data) return;
      setUnreadRemarksCount(data.session_remarks || 0);
      setNewFittingOrdersCount(data.taylor_made || 0);
      setNewCompRegistrations(data.competitions || 0);
      setNewStageRegistrations(data.stages || 0);
      setNewPaymentsCount(data.payments || 0);
      setNewRestaurantCount(data.restaurant || 0);
      setProTasksPendingCount(data.pro_tasks || 0);
      const notifs = data.admin_notifications || {};
      setClientNotifsTotal(notifs.total || 0);
      const cc = data.control_center || {};
      setControlCenterCount((cc.critical || 0) + (cc.warning || 0));
      setControlCenterCritical(cc.critical || 0);
    };

    // S'abonner au service partagé (livrera aussi la valeur en cache immédiatement)
    const unsub = subscribeSidebarCounts(applyCounts);

    // Premier fetch
    fetchSidebarCounts();

    // Refresh toutes les 60s
    const interval = setInterval(() => { fetchSidebarCounts(); }, 60000);

    return () => {
      clearInterval(interval);
      unsub();
    };
  }, [user]);

  const loadOrganizations = async () => {
    if (user?.is_super_admin) {
      try {
        const res = await orgs.list();
        setOrganizations(res.data || []);
      } catch (error) {
        console.error('Error loading organizations:', error);
      }
    }
  };

  const handleSwitchOrg = async (orgId) => {
    if (switchingOrg) return;
    setSwitchingOrg(true);
    try {
      await auth.switchOrganization(orgId);
      const org = organizations.find(o => o.id === orgId);
      toast.success(`Basculé vers ${org?.name || 'le golf'}`);
      setShowOrgDropdown(false);
      if (refreshUser) await refreshUser();
      window.location.reload();
    } catch (error) {
      toast.error('Erreur lors du changement de golf');
    } finally {
      setSwitchingOrg(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Vérifier si l'utilisateur est admin (robuste : user_role, role, is_super_admin)
  const isAdminUser = !!(
    user?.is_super_admin ||
    (user?.user_role === 'admin') ||
    (user?.role === 'admin' || user?.role === 'super_admin') ||
    ((user?.email || '').toLowerCase() === 'aa@bassmanager.com')
  );

  // Vérifier si l'utilisateur a une permission
  const hasPermission = (permission) => {
    // Super admin et admin ont toutes les permissions
    if (isAdminUser || !user?.is_golf_employee) return true;
    // Vérifier les permissions de l'employé Golf
    return user?.permissions?.[permission] === true;
  };

  // Menu items avec permissions requises et couleurs distinctes
  // Permissions utilisées : view_planning, view_courses, view_clients, view_stats, view_portal, view_budget
  // Organisé en sections : Opérationnel, Organisation, Admin
  const allMenuItems = [
    // === OPÉRATIONNEL ===
    { path: '/golf/planning', label: 'Planning', icon: Calendar, permission: 'view_planning', color: 'text-blue-500', section: 'Opérationnel' },
    { path: '/golf/tee-times', label: 'Départs', icon: Flag, permission: 'view_planning', color: 'text-emerald-500', section: 'Opérationnel' },
    { path: '/golf/caisse', label: 'Caisse', icon: ShoppingCart, permission: 'view_stats', color: 'text-emerald-600', section: 'Opérationnel' },
    // { path: '/golf/comptes-clients', label: 'Comptes prépayés', icon: PiggyBank, ...}, // MOVED to /golf/caisse hub
    // { path: '/golf/bons-cadeaux', label: 'Bons cadeaux', icon: Gift, ...}, // MOVED to /golf/caisse hub
    // { path: '/golf/cours', label: 'Cours', icon: BookOpen, ...}, // MOVED to gear icon on /golf/planning
    // { path: '/golf/activity', label: 'Fil', icon: MessageSquare, permission: null, showBadge: 'remarks', color: 'text-cyan-500', section: 'Opérationnel' },
    { path: '/golf/taylor-made', label: 'Taylor Made', icon: ShoppingBag, permission: 'view_stats', showBadge: 'fitting', color: 'text-rose-500', section: 'Opérationnel' },
    { path: '/golf/ecole', label: 'École de Golf', icon: GraduationCap, permission: 'view_clients', color: 'text-amber-600', section: 'Opérationnel' },
    { path: '/golf/competitions', label: 'Événements', icon: Trophy, permission: 'view_courses', showBadge: 'competitions', color: 'text-orange-500', section: 'Opérationnel' },
    { path: '/golf/stages', label: 'Stages', icon: GraduationCap, permission: 'view_courses', showBadge: 'stages', color: 'text-purple-500', section: 'Opérationnel' },
    { path: '/golf/restaurant-reservations', label: 'Restaurant', icon: UtensilsCrossed, permission: 'view_courses', showBadge: 'restaurant', color: 'text-amber-600', section: 'Opérationnel' },
    { path: '/golf/mes-taches', label: 'Mes tâches', icon: ClipboardList, permission: null, showBadge: 'protasks', color: 'text-indigo-500', section: 'Opérationnel' },
    { path: '/golf/notes-de-frais', label: 'Notes de frais', icon: Receipt, permission: 'view_budget', color: 'text-teal-500', section: 'Organisation' },
    
    // === ORGANISATION ===
    { path: '/golf/clients', label: 'Clients', icon: Users, permission: 'view_clients', showBadge: 'client_notifs', color: 'text-amber-500', section: 'Organisation' },
    { path: '/golf/catalogue', label: 'Catalogue', icon: Package, permission: 'view_stats', color: 'text-emerald-600', section: 'Organisation' },
    { path: '/golf/paiements', label: 'Comptabilité', icon: Euro, permission: 'view_stats', showBadge: 'payments', color: 'text-emerald-500', section: 'Organisation' },
    { path: '/golf/control-center', label: 'Centre de contrôle', icon: Shield, permission: 'view_stats', showBadge: 'control_center', color: 'text-red-500', section: 'Organisation' },
    { path: '/golf/stats', label: 'Stats', icon: BarChart3, permission: 'view_stats', color: 'text-teal-500', section: 'Organisation' },
    { path: '/golf/budget', label: 'Budget', icon: PiggyBank, permission: 'view_budget', color: 'text-teal-500', section: 'Organisation' },
    { path: '/golf/resultats', label: 'Résultats', icon: LineChart, permission: 'view_budget', color: 'text-indigo-500', section: 'Organisation' },
    
    // === ADMIN ===
    { path: '/golf/conseiller-ia', label: 'Bass IA', icon: Brain, permission: 'view_stats', color: 'text-purple-500', section: 'Admin' },
    { path: '/golf/actualites', label: 'Actualités', icon: Newspaper, permission: 'view_portal', color: 'text-amber-500', section: 'Admin' },
    { path: '/golf/communication', label: 'Communication', icon: Megaphone, permission: null, color: 'text-pink-500', section: 'Admin' },
    { path: '/golf/devis', label: 'Commercial', icon: FileText, permission: null, color: 'text-teal-500', section: 'Admin' },
    { path: '/golf/site-vitrine', label: 'Site Vitrine', icon: Globe, permission: 'view_portal', color: 'text-emerald-500', section: 'Admin' },
    { path: '/golf/portal-settings', label: 'Parametres', icon: Settings, permission: 'view_portal', color: 'text-slate-500', section: 'Admin' },
    // « Utilisateurs » : accessible depuis la page d'accueil (sous les cartes Golf / Restaurant / Entretien), plus dans ce menu
  ];

  // Filtrer les menus selon les permissions ET page_permissions
  const golfPagePerms = user?.page_permissions?.golf || [];
  const hasPageRestrictions = golfPagePerms.length > 0;
  
  const menuItems = allMenuItems.filter(item => {
    if (item.adminOnly) return isAdminUser;
    // Admin voit tout, quel que soit le reste
    if (isAdminUser) return true;
    const pageId = item.path.replace('/golf/', '');
    // Cas spécial : si des page_permissions explicites existent et que la page est cochée,
    // elles override la permission classique (l'admin a explicitement autorisé l'accès)
    if (hasPageRestrictions && golfPagePerms.includes(pageId)) return true;
    // Vérifier les permissions classiques
    if (item.permission !== null && !hasPermission(item.permission)) return false;
    // Si des restrictions de pages existent, vérifier que la page est autorisée
    if (hasPageRestrictions) {
      return golfPagePerms.includes(pageId);
    }
    return true;
  });

  // Rediriger vers la première page autorisée (ou bloquer l'accès aux pages restreintes)
  useEffect(() => {
    const firstAllowed = menuItems[0]?.path || '/golf/planning';
    const currentPageId = location.pathname.replace('/golf/', '').split('/')[0];

    // Si on est juste sur /golf, rediriger vers la première page autorisée
    if (location.pathname === '/golf' || location.pathname === '/golf/') {
      navigate(firstAllowed, { replace: true });
      return;
    }

    // Si des restrictions existent et la page courante n'est pas autorisée, rediriger
    if (hasPageRestrictions && !isAdminUser) {
      const isAllowed = golfPagePerms.includes(currentPageId) || golfPagePerms.includes(GOLF_SUBPAGE_PARENT[currentPageId]);
      if (!isAllowed) {
        navigate(firstAllowed, { replace: true });
      }
    }
  }, [location.pathname, navigate, menuItems, hasPageRestrictions, golfPagePerms, isAdminUser]);

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');
  
  // Fermer le menu mobile quand on change de page
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);
  
  // Composant NavItems réutilisable
  const NavItems = ({ showLabels = true }) => (
    <nav className="flex-1 overflow-y-auto p-2">
      {['Opérationnel', 'Organisation', 'Admin'].map((section) => {
        const sectionItems = menuItems.filter(item => item.section === section);
        if (sectionItems.length === 0) return null;
        
        return (
          <div key={section} className="mt-3 first:mt-0">
            {showLabels && (
              <p className="px-3 py-1.5 text-xs font-semibold text-neutral-400 uppercase tracking-wider">
                {section}
              </p>
            )}
            {!showLabels && (
              <div className="border-t border-neutral-200 my-2 mx-2"></div>
            )}
            
            {sectionItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              const badgeCount = item.showBadge === 'remarks' ? unreadRemarksCount : 
                                 item.showBadge === 'fitting' ? newFittingOrdersCount :
                                 item.showBadge === 'competitions' ? newCompRegistrations :
                                 item.showBadge === 'stages' ? newStageRegistrations :
                                 item.showBadge === 'payments' ? newPaymentsCount :
                                 item.showBadge === 'protasks' ? proTasksPendingCount :
                                 item.showBadge === 'client_notifs' ? clientNotifsTotal :
                                 item.showBadge === 'control_center' ? controlCenterCount :
                                 item.showBadge === 'restaurant' ? newRestaurantCount : 0;
              // Masquer certaines entrées quand aucune anomalie / rien à voir
              if (item.hideWhenZero && badgeCount === 0 && !active) return null;
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg mb-0.5 transition-colors text-sm ${
                    active 
                      ? 'bg-primary/10 text-primary border-l-4 border-primary' 
                      : 'text-neutral-600 hover:bg-neutral-100'
                  }`}
                  title={!showLabels ? item.label : ''}
                >
                  <div className="relative">
                    <Icon size={18} className={active ? 'text-primary' : ''} />
                    {badgeCount > 0 && (
                      <span className={`absolute -top-1.5 -right-1.5 min-w-[14px] h-3.5 text-white text-[9px] font-bold rounded-full flex items-center justify-center px-0.5 ${
                        item.showBadge === 'fitting' ? 'bg-rose-500' :
                        item.showBadge === 'payments' ? 'bg-emerald-500' : 
                        item.showBadge === 'competitions' ? 'bg-orange-500' :
                        item.showBadge === 'protasks' ? 'bg-red-500' :
                        item.showBadge === 'client_notifs' ? 'bg-red-500' :
                        item.showBadge === 'control_center' ? (controlCenterCritical > 0 ? 'bg-red-500 animate-pulse' : 'bg-amber-500') :
                        item.showBadge === 'stages' ? 'bg-purple-500' : 'bg-amber-500'
                      }`}>
                        {badgeCount > 99 ? '99+' : badgeCount}
                      </span>
                    )}
                  </div>
                  {showLabels && <span className="font-medium">{item.label}</span>}
                </NavLink>
              );
            })}
          </div>
        );
      })}
    </nav>
  );

  const handleGoToPortal = async () => {
    try {
      const adminToken = localStorage.getItem('access_token');
      if (!adminToken) return;
      const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const res = await fetch(`${API_URL}/api/client-portal/admin-as-client`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${adminToken}` },
      });
      if (res.ok) {
        const data = await res.json();
        // Stocker le token client portal AVANT de naviguer
        localStorage.setItem('client_portal_token', data.access_token);
        // Naviguer vers le portail
        window.location.href = clubPath('/');
      } else {
        toast.error('Impossible d\'acceder au portail');
      }
    } catch {
      toast.error('Erreur de connexion au portail');
    }
  };

  const UserFooter = ({ showLabels = true }) => (
    <div className="p-3 border-t border-neutral-200 shrink-0">
      <div className={`flex items-center gap-3 px-3 py-2 ${showLabels ? '' : 'justify-center'}`}>
        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
          <Users size={16} className="text-primary" />
        </div>
        {showLabels && (
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{user?.first_name} {user?.last_name}</p>
            <p className="text-xs text-neutral-500 truncate">{user?.email}</p>
          </div>
        )}
      </div>
      <div className="flex gap-1.5 mt-2">
        <button
          onClick={handleGoToPortal}
          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-colors border border-emerald-200"
          data-testid="golf-portal-link"
        >
          <ExternalLink size={14} />
          {showLabels && <span>Portail</span>}
        </button>
        <button
          onClick={handleLogout}
          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-red-100"
        >
          <LogOut size={14} />
          {showLabels && <span>Quitter</span>}
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen md:h-screen bg-neutral-50" data-testid="golf-module">
      {/* Mobile: Overlay quand menu ouvert */}
      {mobileMenuOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile: Sidebar en drawer (comme Entretien) */}
      <aside className={`
        md:hidden fixed inset-y-0 left-0 z-50 w-72 bg-white border-r border-neutral-200 
        transform transition-transform duration-300 ease-in-out flex flex-col pt-[env(safe-area-inset-top)]
        ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Mobile Sidebar Header */}
        <div className="flex items-center justify-between p-3 border-b border-neutral-200">
          <button
            onClick={() => navigate('/modules')}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            <img src="/bass-logo.png" alt="BASS GOLF" className="w-8 h-8 rounded-lg object-cover" />
            <div>
              <h1 className="text-base font-semibold text-primary">BASS GOLF</h1>
              <p className="text-[9px] text-neutral-400 uppercase tracking-wider -mt-0.5">Module Golf</p>
            </div>
          </button>
          <div className="flex items-center gap-1">
            <button onClick={handleGoToPortal} className="p-2 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-700" title="Portail Client" data-testid="golf-portal-link-mobile">
              <ExternalLink size={18} />
            </button>
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="p-1.5 rounded-lg hover:bg-neutral-100"
              aria-label="Fermer le menu"
            >
              <X size={20} />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          <NavItems showLabels={true} />
        </div>
        <UserFooter showLabels={true} />
      </aside>

      {/* Desktop: Sidebar fixe (comme Entretien) */}
      <aside className={`
        hidden md:flex flex-col relative bg-white border-r border-neutral-200 
        transition-all duration-300 h-full
        ${desktopCollapsed ? 'w-16' : 'w-64'}
      `}>
        {/* Desktop Sidebar Header */}
        <div className="p-3 border-b border-neutral-200 flex items-center justify-between">
          {!desktopCollapsed ? (
            <div className="flex items-center gap-2">
              <button onClick={() => navigate('/modules')} className="hover:opacity-80 transition-opacity">
                <img src="/bass-logo.png" alt="BASS GOLF" className="w-8 h-8 rounded-lg object-cover" />
              </button>
              <div>
                <button onClick={() => navigate('/modules')} className="hover:opacity-80 transition-opacity">
                  <h1 className="text-base font-semibold text-primary">BASS GOLF</h1>
                </button>
                <p className="text-[9px] text-neutral-400 uppercase tracking-wider -mt-0.5">Module Golf</p>
                {/* Sélecteur de golf pour super admin */}
                {user?.is_super_admin ? (
                  <div className="relative">
                    <button
                      onClick={() => setShowOrgDropdown(!showOrgDropdown)}
                      className="flex items-center gap-1 text-xs text-neutral-500 hover:text-primary transition-colors"
                    >
                      <Building2 size={10} />
                      <span className="truncate max-w-[100px]">{user?.organization_name || 'Sélectionner'}</span>
                      <ChevronDown size={10} />
                    </button>
                    
                    {showOrgDropdown && (
                      <div className="absolute top-full left-0 mt-1 w-56 bg-white border border-neutral-200 rounded-lg shadow-lg z-50 py-1">
                        <div className="px-3 py-2 border-b border-neutral-100">
                          <p className="text-xs font-semibold text-neutral-500">CHANGER DE GOLF</p>
                        </div>
                        <div className="max-h-48 overflow-y-auto">
                          {organizations.map(org => (
                            <button
                              key={org.id}
                              onClick={() => handleSwitchOrg(org.id)}
                              disabled={switchingOrg}
                              className={`w-full px-3 py-2 text-left text-sm hover:bg-neutral-50 flex items-center justify-between ${
                                org.id === user?.organization_id ? 'bg-primary/10 text-primary' : 'text-neutral-700'
                              }`}
                            >
                              <span className="truncate">{org.name}</span>
                              {org.id === user?.organization_id && <Check size={14} className="text-primary" />}
                            </button>
                          ))}
                        </div>
                        <div className="border-t border-neutral-100 mt-1 pt-1">
                          <button
                            onClick={() => { setShowOrgDropdown(false); navigate('/all-golfs'); }}
                            className="w-full px-3 py-2 text-left text-sm text-primary hover:bg-primary/10 flex items-center gap-2"
                          >
                            <Globe size={14} />
                            Voir tous les golfs
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-neutral-500">{user?.organization_name || 'Golf'}</p>
                )}
              </div>
            </div>
          ) : (
            <button onClick={() => navigate('/modules')} className="hover:opacity-80 transition-opacity mx-auto">
              <img src="/bass-logo.png" alt="BASS" className="w-8 h-8 rounded-lg object-cover" />
            </button>
          )}
          <button
            onClick={() => setDesktopCollapsed(!desktopCollapsed)}
            className={`p-1.5 rounded-lg hover:bg-neutral-100 ${desktopCollapsed ? 'mx-auto' : ''}`}
            aria-label={desktopCollapsed ? "Agrandir le menu" : "Réduire le menu"}
          >
            {desktopCollapsed ? <Menu size={18} /> : <X size={18} />}
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          <NavItems showLabels={!desktopCollapsed} />
        </div>
        <UserFooter showLabels={!desktopCollapsed} />
      </aside>

      {/* Main content */}
      <main className="flex-1 flex flex-col md:overflow-y-auto overflow-x-hidden">
        {/* Mobile: Header fixe avec hamburger (comme Entretien) */}
        <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-white border-b border-neutral-200 px-3 py-2 flex items-center justify-between" style={{ paddingTop: 'max(0.5rem, env(safe-area-inset-top))' }}>
          {/* Bouton hamburger */}
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="p-2 rounded-lg hover:bg-neutral-100"
            aria-label="Ouvrir le menu"
          >
            <Menu size={22} />
          </button>
          
          <button
            onClick={() => navigate('/modules')}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            <img src="/bass-logo.png" alt="BASS GOLF" className="w-7 h-7 rounded-lg object-cover" />
            <div>
              <span className="font-semibold text-primary block text-sm">BASS GOLF</span>
              <span className="text-[8px] text-neutral-400 uppercase tracking-wider -mt-1 block">Module Golf</span>
            </div>
          </button>
          
          {/* Sélecteur de golf à droite */}
          <div className="flex items-center gap-1">
            <AdminControlCenterBell bellSize={18} />
            <NotificationInbox userId={user?.id} target="admin" bellSize={18} userType="admin" />
            <div className="relative">
            <button
              onClick={() => setShowOrgDropdown(!showOrgDropdown)}
              className="flex items-center gap-1 p-2 rounded-lg hover:bg-neutral-100"
            >
              <Building2 size={18} className="text-neutral-500" />
              <ChevronDown size={12} className="text-neutral-400" />
            </button>

            {showOrgDropdown && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowOrgDropdown(false)} />
                <div className="absolute top-full right-0 mt-2 w-64 bg-white border border-neutral-200 rounded-lg shadow-xl z-50 py-1">
                  <div className="px-3 py-2 border-b border-neutral-100">
                    <p className="text-xs font-semibold text-neutral-500 uppercase">Changer de golf</p>
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    {organizations.map(org => (
                      <button
                        key={org.id}
                        onClick={() => handleSwitchOrg(org.id)}
                        disabled={switchingOrg}
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-neutral-50 flex items-center justify-between ${
                          org.id === user?.organization_id ? 'bg-primary/10 text-primary' : 'text-neutral-700'
                        }`}
                      >
                        <span className="truncate">{org.name}</span>
                        {org.id === user?.organization_id && <Check size={14} className="text-primary" />}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
          </div>
        </div>

        {/* Spacer pour le header mobile fixe */}
        <div className="md:hidden h-14" style={{ marginTop: 'env(safe-area-inset-top)' }}></div>

        {/* Desktop header avec cloche notifications */}
        <div className="hidden md:flex fixed top-0 right-0 z-50 bg-white border-b border-neutral-200 h-12 items-center px-6 justify-between"
          style={{ left: desktopCollapsed ? '4rem' : '16rem' }}
        >
          <div className="flex items-center gap-2">
            <Flag size={18} className="text-primary" />
            <span className="font-medium text-neutral-700">Module Golf</span>
          </div>
          <div className="flex items-center gap-2">
            <AdminControlCenterBell />
            <NotificationInbox userId={user?.id} target="admin" userType="admin" />
          </div>
        </div>
        {/* Spacer desktop header */}
        <div className="hidden md:block h-12"></div>

        {/* Page content */}
        <div className="flex-1 p-2 md:p-3 overflow-y-auto pb-4">
          <Outlet context={{ setNewCompRegistrations, setNewStageRegistrations }} />
        </div>
      </main>
      {/* Rappel automatique des tâches à 7h/12h/18h */}
      <TaskReminderModal />
    </div>
  );
};

export default GolfModule;
